import React, { useState } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { Button } from "src/design-system/Button";
import { toastActions } from "src/design-system/Toast/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { useSaveUserPermissions } from "src/userManagement/userPermissions/queries";
import {
  getRoleOperations,
  UserPermissionsFormValues,
} from "src/userManagement/userPermissions/utils";
import { errorMessage } from "src/utils/stringUtils";

type Props = {
  userId: string;
  initialFormValues: UserPermissionsFormValues;
};

export const UserPermissionsActions: React.FC<Props> = ({
  userId,
  initialFormValues,
}) => {
  const { orgId, workspace } = useWorkspaceContext();
  const { control, getValues, reset } =
    useFormContext<UserPermissionsFormValues>();
  const [savedFormValues, setSavedFormValues] = useState(initialFormValues);
  const currentFormValues = useWatch({ control });
  const saveUserPermissions = useSaveUserPermissions({ userId });

  const operations = getRoleOperations({
    baseline: savedFormValues,
    current: currentFormValues,
    orgId,
    workspaceId: workspace.id,
  });
  const orgRole =
    currentFormValues.orgRole === savedFormValues.orgRole
      ? undefined
      : currentFormValues.orgRole;

  const handleSave = () =>
    saveUserPermissions.mutate(
      { operations, orgRole },
      {
        onSuccess: () => {
          const saved = getValues();
          setSavedFormValues(saved);
          reset(saved);
          toastActions.success({ title: "Permissions updated" });
        },
        onError: (error) =>
          toastActions.failure({
            title: "Failed to update permissions",
            description: errorMessage(error),
          }),
      },
    );

  if (operations.length === 0 && !orgRole) {
    return null;
  }

  return (
    <div className="flex items-center gap-x-2 pr-4">
      <Button
        dataLoc="user-permissions-cancel"
        isDisabled={saveUserPermissions.isPending}
        size="sm"
        variant="secondary"
        onClick={() => reset(savedFormValues)}
      >
        Cancel
      </Button>
      <Button
        dataLoc="user-permissions-save"
        isLoading={saveUserPermissions.isPending}
        size="sm"
        onClick={handleSave}
      >
        Save changes
      </Button>
    </div>
  );
};
